const PERMS = ["leads", "masters", "universities", "destinations", "accounts", "ai", "chat", "settings", "employees", "agents"];

export const ENTITIES = {
  countries: {
    title: "Countries", singular: "Country", subtitle: "Manage study destinations & office countries",
    endpoint: "/masters/countries",
    columns: [
      { key: "name", label: "Name" }, { key: "code", label: "Code" },
      { key: "dial_code", label: "Dial" }, { key: "currency", label: "Currency" },
      { key: "active", label: "Status", type: "bool" },
    ],
    fields: [
      { key: "name", label: "Country Name", required: true },
      { key: "code", label: "ISO Code (e.g. MY)", required: true },
      { key: "dial_code", label: "Dial Code (e.g. +60)", required: true },
      { key: "flag", label: "Flag Emoji" },
      { key: "currency", label: "Currency" },
      { key: "active", label: "Active", type: "switch" },
    ],
  },
  companies: {
    title: "Companies", singular: "Company", subtitle: "One company entity per country",
    endpoint: "/masters/companies",
    columns: [
      { key: "name", label: "Company" }, { key: "country_id", label: "Country", ref: "countries" },
      { key: "phone", label: "Tel" }, { key: "email", label: "Email" },
      { key: "show_on_header", label: "On Header", type: "bool" }, { key: "active", label: "Status", type: "bool" },
    ],
    fields: [
      { key: "name", label: "Company Name", required: true },
      { key: "country_id", label: "Country", ref: "countries", required: true },
      { key: "reg_no", label: "Registration No." },
      { key: "email", label: "Email Address" }, { key: "phone", label: "Telephone No." },
      { key: "website", label: "Website URL" },
      { key: "address", label: "Address", type: "textarea" },
      { key: "show_on_header", label: "Show this company's phone & email on the website header (only one company at a time)", type: "switch" },
      { key: "active", label: "Active", type: "switch" },
    ],
  },
  offices: {
    title: "Offices", singular: "Office", subtitle: "Branch offices per country & company",
    endpoint: "/masters/offices",
    columns: [
      { key: "name", label: "Office" }, { key: "country_id", label: "Country", ref: "countries" },
      { key: "company_id", label: "Company", ref: "companies" }, { key: "city", label: "City" }, { key: "active", label: "Status", type: "bool" },
    ],
    fields: [
      { key: "name", label: "Office Name", required: true },
      { key: "country_id", label: "Country", ref: "countries", required: true },
      { key: "company_id", label: "Company", ref: "companies" },
      { key: "city", label: "City" }, { key: "phone", label: "Phone" },
      { key: "address", label: "Address", type: "textarea" },
      { key: "active", label: "Active", type: "switch" },
    ],
  },
  roles: {
    title: "Roles", singular: "Role", subtitle: "Role-based permissions, country specific",
    endpoint: "/masters/roles",
    columns: [
      { key: "name", label: "Role" }, { key: "country_id", label: "Country", ref: "countries" },
      { key: "permissions", label: "Permissions", render: (r) => (r.permissions || []).length + " modules" },
    ],
    fields: [
      { key: "name", label: "Role Name", required: true },
      { key: "country_id", label: "Country (blank = global)", ref: "countries" },
      { key: "permissions", label: "Permissions", type: "multiselect", options: PERMS, default: [] },
      { key: "description", label: "Description", type: "textarea" },
    ],
  },
  designations: {
    title: "Designations", singular: "Designation", subtitle: "Job titles for personnel",
    endpoint: "/masters/designations",
    columns: [
      { key: "name", label: "Designation" }, { key: "country_id", label: "Country", ref: "countries" },
      { key: "description", label: "Description" },
    ],
    fields: [
      { key: "name", label: "Designation", required: true },
      { key: "country_id", label: "Country (blank = global)", ref: "countries" },
      { key: "description", label: "Description", type: "textarea" },
    ],
  },
  employees: {
    title: "Employees", singular: "Employee", subtitle: "Counsellors & staff — email + phone verified login",
    endpoint: "/masters/employees",
    columns: [
      { key: "name", label: "Name" }, { key: "email", label: "Email" },
      { key: "country_id", label: "Country", ref: "countries" }, { key: "role_id", label: "Role", ref: "roles" },
      { key: "on_leave", label: "On Leave", render: (r) => (r.on_leave ? "On Leave" : "\u2014") },
      { key: "active", label: "Status", type: "bool" },
    ],
    fields: [
      { key: "name", label: "Full Name", required: true },
      { key: "email", label: "Email (login)", required: true },
      { key: "phone", label: "Phone", type: "phone", required: true },
      { key: "password", label: "Password", type: "password" },
      { key: "country_id", label: "Country", ref: "countries", required: true },
      { key: "company_id", label: "Company", ref: "companies" },
      { key: "office_id", label: "Office", ref: "offices" },
      { key: "role_id", label: "Role", ref: "roles" },
      { key: "designation_id", label: "Designation", ref: "designations" },
      { key: "reports_to", label: "Reports To (manager)", ref: "employees" },
      { key: "active", label: "Active (uncheck to deactivate the login)", type: "switch" },
      { key: "on_leave", label: "On Leave (temporarily away — show a replacement contact)", type: "switch", default: false },
      { key: "replacement_employee_id", label: "Temporary Replacement (colleague to contact)", ref: "employees" },
      { key: "replacement_contact", label: "Replacement Contact (phone/email, if outside the team)" },
      { key: "leave_until", label: "On Leave Until (e.g. 2026-07-15)" },
    ],
  },
  agents: {
    title: "Agents", singular: "Agent", subtitle: "Partner agents with their own verified login", approval: true,
    endpoint: "/masters/agents",
    columns: [
      { key: "name", label: "Name" }, { key: "agency_name", label: "Agency" },
      { key: "agent_code", label: "Code" },
      { key: "email_verified", label: "Email", render: (r) => (r.email_verified ? "\u2713 Verified" : "\u23f3 Pending") },
      { key: "status", label: "Status", render: (r) => r.status || (r.active ? "approved" : "—") },
      { key: "country_id", label: "Country", ref: "countries" },
      { key: "active", label: "Active", type: "bool" },
    ],
    fields: [
      { key: "name", label: "Full Name", required: true },
      { key: "agency_name", label: "Agency Name" },
      { key: "email", label: "Email (login)", required: true },
      { key: "phone", label: "Phone", type: "phone", required: true },
      { key: "password", label: "Password", type: "password" },
      { key: "country_id", label: "Country", ref: "countries", required: true },
      { key: "company_id", label: "Company", ref: "companies" },
      { key: "office_id", label: "Office", ref: "offices" },
      { key: "role_id", label: "Role", ref: "roles" },
      { key: "designation_id", label: "Designation", ref: "designations" },
      { key: "commission_pct", label: "Commission %", type: "number" },
      { key: "contact_person", label: "Contact Person" },
      { key: "contact_person_email", label: "Contact Person Email", type: "email" },
      { key: "contact_person_phone", label: "Contact Person Mobile", type: "phone", dialKey: "contact_person_dial" },
      { key: "students_placed", label: "Students Placed (past performance)", type: "number" },
      { key: "new_to_field", label: "New to this field?", type: "options", options: ["No", "Yes"], default: "No" },
      { key: "previous_business", label: "If new — your earlier line of business", type: "textarea" },
      { key: "experience_details", label: "If experienced — experience details", type: "textarea" },
      { key: "experience_docs", label: "Experience / past-performance certificates", type: "docs" },
      { key: "active", label: "Active", type: "switch" },
    ],
  },
  teachers: {
    title: "Teachers", singular: "Teacher", subtitle: "Faculty for GDIH's own curriculum \u2014 verified login",
    endpoint: "/masters/teachers",
    columns: [
      { key: "name", label: "Name" }, { key: "subject", label: "Subject" }, { key: "email", label: "Email" },
      { key: "country_id", label: "Country", ref: "countries" }, { key: "active", label: "Status", type: "bool" },
    ],
    fields: [
      { key: "name", label: "Full Name", required: true },
      { key: "email", label: "Email (login)", required: true },
      { key: "phone", label: "Phone", type: "phone", required: true },
      { key: "password", label: "Password", type: "password" },
      { key: "subject", label: "Subject / Specialisation" },
      { key: "qualification", label: "Qualification" },
      { key: "country_id", label: "Country", ref: "countries", required: true },
      { key: "company_id", label: "Company", ref: "companies" },
      { key: "active", label: "Active", type: "switch" },
    ],
  },
  students: {
    title: "Students", singular: "Student", subtitle: "Students who self-registered or were added by admin",
    endpoint: "/masters/students", viewProfile: true,
    columns: [
      { key: "name", label: "Name" }, { key: "email", label: "Email" },
      { key: "phone", label: "Phone", render: (r) => `${r.dial_code || ""} ${r.phone || ""}` },
      { key: "interest", label: "Interest" }, { key: "country", label: "Country" },
    ],
    fields: [
      { key: "name", label: "Full Name", required: true },
      { key: "email", label: "Email (login)", required: true },
      { key: "phone", label: "Phone", type: "phone", required: true },
      { key: "password", label: "Password", type: "password" },
      { key: "gender", label: "Gender", type: "options", options: ["Female", "Male"] },
      { key: "dob", label: "Date of Birth (YYYY-MM-DD)" },
      { key: "nationality", label: "Nationality" },
      { key: "country_id", label: "Country", ref: "countries" },
      { key: "interest", label: "Course of Interest" },
      { key: "agent_code", label: "Agent Code", default: "DIRECT" },
      { key: "active", label: "Active", type: "switch" },
    ],
  },
  edu_courses: {
    title: "Course Catalog", singular: "Course", subtitle: "Level \u2192 Stream \u2192 Course master used by student profiles",
    endpoint: "/academics/edu-courses",
    columns: [
      { key: "level", label: "Level" }, { key: "stream", label: "Stream" },
      { key: "course", label: "Course" }, { key: "active", label: "Status", type: "bool" },
    ],
    fields: [
      { key: "level", label: "Level", type: "options", required: true,
        options: ["High School", "Diploma", "Bachelor's", "Master's", "Doctoral (PhD)"] },
      { key: "stream", label: "Stream (e.g. Engineering)", required: true },
      { key: "course", label: "Course (e.g. B.Tech Computer Science)", required: true },
      { key: "active", label: "Active", type: "switch" },
    ],
  },
  institutions: {
    title: "Previous Schools", singular: "Previous School", subtitle: "Schools / colleges students pick from in their academic history",
    endpoint: "/academics/institutions",
    columns: [
      { key: "name", label: "Institution" }, { key: "country", label: "Country" },
      { key: "city", label: "City" }, { key: "active", label: "Status", type: "bool" },
    ],
    fields: [
      { key: "name", label: "Institution Name", required: true },
      { key: "country", label: "Country" }, { key: "city", label: "City" },
      { key: "address", label: "Address", type: "textarea" },
      { key: "active", label: "Active", type: "switch" },
    ],
  },
  odl_courses: {
    title: "ODL Courses", singular: "ODL Course", subtitle: "Online & distance learning / certificate courses (public catalogue)",
    endpoint: "/academics/odl-courses",
    columns: [
      { key: "name", label: "Course" }, { key: "category", label: "Category" },
      { key: "price", label: "Price" }, { key: "active", label: "Status", type: "bool" },
    ],
    fields: [
      { key: "name", label: "Course Name", required: true },
      { key: "category", label: "Category", type: "options", options: ["Programme", "Micro-Credential", "Online Course", "Professional Certification", "ESG Short Course"] },
      { key: "price", label: "Price (e.g. RM 3,000)" },
      { key: "provider", label: "Provider", default: "UCSI University (ODL)" },
      { key: "desc", label: "Description", type: "textarea" },
      { key: "active", label: "Active", type: "switch" },
    ],
  },
  certificates: {
    title: "Certificates", singular: "Certificate", subtitle: "Issued to GDIH graduates — publicly verifiable at /verify",
    endpoint: "/academics/certificates",
    pdfField: "certificate_no",
    bulkImport: {
      endpoint: "/academics/certificates/bulk",
      headers: ["certificate_no", "student_name", "student_code", "programme", "institution", "grade", "issue_date", "status"],
      sample: "GDIH-CERT-1001,Jane Doe,GDIH-MY-000101,BSc Computer Science,UCSI University,First Class,2025-06-01,Valid",
    },
    columns: [
      { key: "certificate_no", label: "Cert No." }, { key: "student_name", label: "Student" },
      { key: "programme", label: "Programme" }, { key: "status", label: "Status" },
    ],
    fields: [
      { key: "certificate_no", label: "Certificate Number", required: true },
      { key: "student_name", label: "Student Name", required: true },
      { key: "student_code", label: "Student Reference Code" },
      { key: "programme", label: "Programme", required: true },
      { key: "institution", label: "Institution" },
      { key: "grade", label: "Grade / Result" },
      { key: "issue_date", label: "Issue Date (e.g. 2025-06-01)" },
      { key: "status", label: "Status", type: "options", options: ["Valid", "Revoked"], default: "Valid" },
      { key: "notes", label: "Notes", type: "textarea" },
    ],
  },
  fee_master: {
    title: "Fee Master", singular: "Fee", subtitle: "Application fees per university, level & course",
    endpoint: "/academics/fee-master",
    columns: [
      { key: "university_name", label: "University" }, { key: "level", label: "Level" },
      { key: "course", label: "Course" }, { key: "amount", label: "Amount" },
      { key: "currency", label: "Currency" }, { key: "active", label: "Status", type: "bool" },
    ],
    fields: [
      { key: "university_id", label: "University", ref: "universities", required: true },
      { key: "level", label: "Level", type: "options", options: ["Foundation", "Diploma", "Undergraduate", "Postgraduate", "PhD"] },
      { key: "course", label: "Course / Programme (blank = whole level)" },
      { key: "amount", label: "Fee Amount", type: "number", required: true },
      { key: "currency", label: "Currency", type: "options", options: ["USD", "EUR", "GBP", "MYR", "INR"], default: "USD" },
      { key: "active", label: "Active", type: "switch" },
    ],
  },
  testimonials: {
    title: "Testimonials", singular: "Testimonial", subtitle: "Student & alumni reviews — approve to show publicly",
    endpoint: "/academics/testimonials",
    columns: [
      { key: "name", label: "Name" }, { key: "programme", label: "Programme" },
      { key: "rating", label: "Rating" }, { key: "approved", label: "Approved", type: "bool" },
    ],
    fields: [
      { key: "name", label: "Name", required: true },
      { key: "programme", label: "Programme" },
      { key: "rating", label: "Rating (1-5)", type: "number", default: 5 },
      { key: "text", label: "Testimonial", type: "textarea", required: true },
      { key: "approved", label: "Approved (show on public site)", type: "switch" },
    ],
  },
  parents: {
    title: "Parents", singular: "Parent", subtitle: "Parent/guardian logins linked to a student",
    endpoint: "/academics/parents",
    columns: [
      { key: "name", label: "Name" }, { key: "email", label: "Email" },
      { key: "student_code", label: "Student Ref" }, { key: "active", label: "Status", type: "bool" },
    ],
    fields: [
      { key: "name", label: "Full Name", required: true },
      { key: "email", label: "Email", required: true },
      { key: "phone", label: "Phone", type: "phone" },
      { key: "student_code", label: "Student Reference Code (e.g. GDIH-MY-000101)" },
      { key: "password", label: "Password", type: "password" },
      { key: "active", label: "Active", type: "switch" },
    ],
  },
  guardian_relations: {
    title: "Guardian Relationships", singular: "Relationship", subtitle: "Relationship options students pick for their guardian (Father, Mother, Sponsor…)",
    endpoint: "/masters/guardian-relations",
    columns: [{ key: "name", label: "Relationship" }, { key: "active", label: "Status", type: "bool" }],
    fields: [
      { key: "name", label: "Relationship (e.g. Father)", required: true },
      { key: "active", label: "Active", type: "switch" },
    ],
  },
  currencies: {
    title: "Currencies", singular: "Currency", subtitle: "Currencies for referral fees & payments (base: USD)",
    endpoint: "/masters/currencies",
    columns: [{ key: "code", label: "Code" }, { key: "name", label: "Name" }, { key: "symbol", label: "Symbol" }, { key: "active", label: "Status", type: "bool" }],
    fields: [
      { key: "code", label: "Code (e.g. USD)", required: true },
      { key: "name", label: "Name", required: true },
      { key: "symbol", label: "Symbol" },
      { key: "active", label: "Active", type: "switch" },
    ],
  },
  guardians: {
    title: "Guardians", singular: "Guardian", subtitle: "Reusable guardian directory students can pick from",
    endpoint: "/masters/guardians",
    columns: [
      { key: "name", label: "Name" }, { key: "relationship", label: "Relationship", ref: "guardian-relations" },
      { key: "phone", label: "Phone", render: (r) => `${r.dial_code || ""} ${r.phone || ""}` },
      { key: "email", label: "Email" }, { key: "active", label: "Status", type: "bool" },
    ],
    fields: [
      { key: "name", label: "Guardian Name", required: true },
      { key: "relationship", label: "Relationship", ref: "guardian-relations" },
      { key: "phone", label: "Phone", type: "phone" },
      { key: "email", label: "Email" },
      { key: "passport_no", label: "Passport No" },
      { key: "country", label: "Country" },
      { key: "city", label: "City" },
      { key: "active", label: "Active", type: "switch" },
    ],
  },
  fields: {
    title: "Fields / Specialisations", singular: "Field", subtitle: "Specialisations students pick within a course — used by the University master",
    endpoint: "/academics/fields",
    columns: [{ key: "name", label: "Field" }, { key: "active", label: "Status", type: "bool" }],
    fields: [
      { key: "name", label: "Field name", required: true },
      { key: "active", label: "Active", type: "switch" },
    ],
  },
  streams: {
    title: "Streams", singular: "Stream", subtitle: "Level → Stream → Course cascade source (e.g. Science, Commerce, Engineering)",
    endpoint: "/academics/streams",
    columns: [{ key: "name", label: "Stream" }, { key: "level", label: "Level" }, { key: "active", label: "Status", type: "bool" }],
    fields: [
      { key: "name", label: "Stream name", required: true },
      { key: "level", label: "Level (optional — limits this stream to one level)", type: "options", options: ["Diploma", "Bachelor's", "Master's", "Doctoral (PhD)", "Foundation", "Undergraduate", "Postgraduate", "PhD"] },
      { key: "active", label: "Active", type: "switch" },
    ],
  },
};